import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Drawer, Menu } from "antd";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { cognitoUserPool } from "config";
import "styles/account.scss";
import useCommonStore from "stores/useCommon";
import API from "services/api";
import {
  clearUserInfo,
  clearUserToken,
  getConfirmationEmail,
} from "services/storages/userStorage";
import { personaeOptions } from "constants/account";
import { useGetAccountQuery } from "queries/useGetAccountQuery";
import { useAccountMutation } from "mutations/useAccountMutation";
import { AccountData } from "types/AccountData.type";
import { Link, useNavigate } from "react-router-dom";
import PAGES from "routes/constants";
import QUERY_KEYS from "services/api/queryKeys";

function AccountPage() {
  const navigate = useNavigate();
  const [accountData, setAccountData] = useState<AccountData>();
  const userInfoAWS = cognitoUserPool.getCurrentUser();
  const confirmationEmail = getConfirmationEmail();
  const email = userInfoAWS?.getUsername() || confirmationEmail;
  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const { data: result } = useQuery({
    queryKey: [QUERY_KEYS.GET_PROJECTS],
    queryFn: API.mock.getProjects,
    retry: 0,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const projects = result?.projects || [];

  if (!userInfoAWS) {
    navigate(PAGES.login);
  }

  const {
    data: account,
    isSuccess: getAccountSuccess,
    error: fetchAccountError,
  } = useGetAccountQuery(email);
  const { mutate } = useAccountMutation();
  const { setAlertTop, closeAlertTop } = useCommonStore();

  useEffect(() => {
    setAccountData(account);
  }, [account, getAccountSuccess, fetchAccountError]);

  const handleUpdate = async () => {
    if (!email || !accountData) return;
    mutate(accountData, {
      onSuccess: () => {
        setAlertTop({
          type: "info",
          message: "Vos informations ont été mises à jour avec succès.",
          closeable: true,
        });
        setTimeout(() => {
          closeAlertTop();
        }, 5000);
      },
      onError: () => {
        setAlertTop({
          type: "warning",
          message:
            "La mise à jour de vos informations a échoué. Veuillez réessayer.",
          closeable: true,
        });
        setTimeout(() => {
          closeAlertTop();
        }, 5000);
      },
    });
  };

  const handleDisconnect = async () => {
    clearUserInfo();
    clearUserToken();
    userInfoAWS?.signOut();
    navigate(PAGES.home);
  };

  const handleCloseMenu = () => {
    setIsOpenMenu(false);
  };

  const handleClickMenu = ({ key }: any) => {
    setIsOpenMenu(false);
    navigate(key);
  };

  return (
    <div className="energy-account">
      {accountData && (
        <div className="energy-account__content">
          <div className="profile-options">
            <h2>Votre profil</h2>
            <form id="account-form">
              <div className="input-stack">
                <input
                  type="text"
                  value={accountData.email}
                  placeholder="Email"
                  className="form-input"
                  disabled
                />
                <input
                  type="text"
                  value={accountData.first_name}
                  onChange={(e) =>
                    setAccountData({
                      ...accountData,
                      first_name: e.target.value,
                    })
                  }
                  placeholder="Prénom"
                  className="form-input"
                />
                <input
                  type="text"
                  value={accountData.last_name}
                  onChange={(e) =>
                    setAccountData({
                      ...accountData,
                      last_name: e.target.value,
                    })
                  }
                  placeholder="Nom"
                  className="form-input"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    value="*******"
                    placeholder="Mot de passe"
                    className="form-input"
                    disabled
                  />
                  <Link to={PAGES.forgotPassword}>
                    <Button className="h-[52px]">Modifier</Button>
                  </Link>
                </div>
              </div>
            </form>

            <Card className="bg-[#f5f5f5] mb-8 card-projects">
              <div className="flex justify-between items-center">
                <Button
                  type="primary"
                  className="bg-[#2a604c] hover:!bg-[#32725b]"
                  onClick={() =>
                    (window as any).openMainMenu
                      ? (window as any).openMainMenu()
                      : setIsOpenMenu(!isOpenMenu)
                  }
                >
                  Mes DPEs
                </Button>
                <div className="text-right">
                  
                  {projects.length >= 5 && (
                    <p className="text-base">Les 5 plus récents</p>
                  )}
                </div>
              </div>

              <div className="bg-white p-4 mt-4 flex flex-col gap-2.5">
                <div
                  className="overflow-x-auto" // scroll wrapper
                >
                  <div className="grid grid-cols-3 gap-4 w-max md:w-auto text-[#707070]">
                    {!!projects.length &&
                      projects.map((project: any) => (
                        <React.Fragment key={project.ref_ademe}>
                          <Link
                            className="font-normal text-sm"
                            to={PAGES.dashboard.replace(
                              ":id",
                              project.ref_ademe
                            )}
                          >
                            {project.ref_ademe}
                          </Link>
                          <p>
                            {dayjs(project.hr_ts).format("DD/MM/YYYY HH[h]mm")}
                          </p>
                          <p>{project.cd_type}</p>
                        </React.Fragment>
                      ))}
                  </div>
                </div>
              </div>
            </Card>

            {personaeOptions.map((option) => (
              <div key={option.id} className="option-item">
                <label>
                  <input
                    type="radio"
                    name="personae"
                    value={option.id}
                    checked={accountData.personae === option.id}
                    onChange={(e) =>
                      setAccountData({
                        ...accountData,
                        personae: Number(e.target.value),
                      })
                    }
                  />
                  <div>
                    <div className="option-title">{option.label}</div>
                    <div className="option-description">
                      {option.description}
                    </div>
                  </div>
                </label>
              </div>
            ))}

            <button onClick={handleUpdate} className="btn-update">
              Mettre à jour
            </button>
            <button
              onClick={handleDisconnect}
              className="btn-update btn-disconnect"
            >
              Se déconnecter
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default AccountPage;